import { useCallback, useMemo } from "react";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  DEFAULT_PAGE_SIZE,
  QUERY_GC_TIME,
  QUERY_RETRY,
  QUERY_STALE_TIME,
  getRetryDelay,
} from "@/lib/constants/query-config";
import { proposalQueryKeys } from "@/lib/query/query-keys";
import { buildFallbackPagination } from "@/lib/utils/pagination";
import { logger } from "@/lib/utils/logger";

import type {
  ProposalMetricsSummary,
  ProposalQueryFilters,
  ProposalsQueryResult,
  UseProposalsParams,
} from "./types";
import { fetchProposalMetrics, fetchProposals, useProposalsApi } from "./api";
import {
  getProposalSort,
  getProposalsQueryKey,
  normalizeMetricsFilters,
  useProposalFilters,
} from "./query-options";

export function useProposals(params: UseProposalsParams = {}) {
  const page = params.page ?? 1;
  const limit = params.limit ?? DEFAULT_PAGE_SIZE;
  const enabled = params.enabled ?? true;

  const { api, organizationId, queryEnabled } = useProposalsApi({
    enabled,
    debugKey: "useProposals",
  });
  const queryClient = useQueryClient();

  const filters = useProposalFilters(params);
  const sort = getProposalSort(params);

  const queryKey = useMemo(
    () => getProposalsQueryKey(organizationId, filters, sort, page, limit),
    [organizationId, filters, sort, page, limit],
  );

  const {
    data: proposalResult,
    isLoading,
    error,
    isError,
    refetch,
    isFetching,
  } = useQuery<ProposalsQueryResult>({
    queryKey,
    enabled: queryEnabled,
    placeholderData: keepPreviousData,
    retry: QUERY_RETRY.DEFAULT,
    retryDelay: getRetryDelay,
    staleTime: QUERY_STALE_TIME.STANDARD,
    gcTime: QUERY_GC_TIME.STANDARD,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    meta: { entity: "proposals" },
    queryFn: async () =>
      fetchProposals(api, {
        page,
        limit,
        filters,
        sort,
      }),
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: proposalQueryKeys.lists(),
      refetchType: "active",
    });
  }, [queryClient]);

  const proposals = proposalResult?.data ?? [];
  const pagination =
    proposalResult?.pagination ??
    buildFallbackPagination(page, limit, proposals.length);

  return {
    data: proposals,
    proposals,
    pagination,
    isLoading,
    isFetching,
    isError,
    error: isError && error instanceof Error ? error.message : null,
    refetch,
    invalidate,
  };
}

export interface ProposalMetricsParams extends Pick<
  ProposalQueryFilters,
  | "status"
  | "projectId"
  | "estimateId"
  | "ownerId"
  | "search"
  | "startDate"
  | "endDate"
> {
  authorId?: string;
}

export function useProposalsMetrics(
  params: ProposalMetricsParams = {},
  options: { enabled?: boolean } = {},
) {
  const { api, organizationId, queryEnabled } = useProposalsApi({
    enabled: options.enabled ?? true,
    debugKey: "useProposalsMetrics",
  });

  const normalizedFilters = useMemo(
    () =>
      normalizeMetricsFilters({
        status: params.status,
        projectId: params.projectId,
        estimateId: params.estimateId,
        authorId: params.authorId ?? params.ownerId,
        startDate: params.startDate,
        endDate: params.endDate,
        search: params.search,
      }),
    [
      params.status,
      params.projectId,
      params.estimateId,
      params.authorId,
      params.ownerId,
      params.startDate,
      params.endDate,
      params.search,
    ],
  );

  const metricsQueryKey = useMemo(
    () => getMetricsQueryKey(organizationId, normalizedFilters),
    [organizationId, normalizedFilters],
  );

  return useQuery<ProposalMetricsSummary>({
    queryKey: metricsQueryKey,
    enabled: queryEnabled && (options.enabled ?? true),
    staleTime: QUERY_STALE_TIME.STANDARD,
    gcTime: QUERY_GC_TIME.STANDARD,
    retry: QUERY_RETRY.AGGRESSIVE,
    retryDelay: getRetryDelay,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      if (!organizationId) {
        throw new Error("Organization ID is required");
      }

      try {
        return await fetchProposalMetrics(api, normalizedFilters);
      } catch (err) {
        logger.error("Failed to fetch proposal metrics", {
          error: err instanceof Error ? err.message : err,
        });
        throw err;
      }
    },
  });
}

function getMetricsQueryKey(
  organizationId: string | null,
  filters: ProposalQueryFilters,
) {
  return proposalQueryKeys.metrics(organizationId ?? undefined, filters);
}

export type { ProposalMetricsSummary } from "./types";
